import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, 
  faChartLine, 
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

/**
 * MonteCarloResults Component
 * 
 * Displays Monte Carlo simulation results with:
 * - Statistical summary (mean, median, percentiles)
 * - Distribution histogram
 * - Confidence intervals
 * - Comparison with deterministic results
 * - Risk analysis
 */
const MonteCarloResults = ({ results }) => {
  if (!results || results.mode !== 'monte_carlo') {
    return null;
  }

  const { statistics, distribution, deterministicComparison, iterations } = results;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };


  // Calculate confidence interval width
  const confidenceIntervalWidth = statistics.totalCost.p90 - statistics.totalCost.p10;
  const confidenceIntervalPercent = (confidenceIntervalWidth / statistics.totalCost.mean) * 100;

  // Risk assessment based on variance
  const getRiskLevel = () => {
    const cvPercent = (statistics.totalCost.stdDev / statistics.totalCost.mean) * 100;
    if (cvPercent < 5) return { level: 'Low', color: 'green', icon: faCheckCircle };
    if (cvPercent < 10) return { level: 'Medium', color: 'yellow', icon: faInfoCircle };
    return { level: 'High', color: 'red', icon: faExclamationTriangle };
  };

  const risk = getRiskLevel();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">🎲 Monte Carlo Simulation Results</h2>
            <p className="text-purple-100">
              Probabilistic analysis based on {iterations.toLocaleString()} iterations
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-purple-100">Risk Level</div>
            <div className={`inline-flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-lg mt-1`}>
              <FontAwesomeIcon icon={risk.icon} className="mr-2" />
              <span className="font-bold text-lg">{risk.level}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Mean Cost */}
        <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-blue-200">
          <div className="text-sm text-gray-600 mb-1">Mean (Expected) Cost</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalCost.mean)}</div>
          <div className="text-xs text-gray-500 mt-1">Average across all iterations</div>
        </div>

        {/* Median Cost */}
        <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-purple-200">
          <div className="text-sm text-gray-600 mb-1">Median (P50) Cost</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalCost.median)}</div>
          <div className="text-xs text-gray-500 mt-1">50% of outcomes below this</div>
        </div>

        {/* P90 Cost */}
        <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-orange-200">
          <div className="text-sm text-gray-600 mb-1">P90 Budget</div>
          <div className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.totalCost.p90)}</div>
          <div className="text-xs text-gray-500 mt-1">90% confidence level</div>
        </div>

        {/* Range */}
        <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Cost Range</div>
          <div className="text-sm font-bold text-gray-900">
            {formatCurrency(statistics.totalCost.min)}
            <div className="text-xs text-gray-500 my-1">to</div>
            {formatCurrency(statistics.totalCost.max)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Min - Max range</div>
        </div>
      </div>

      {/* Confidence Intervals Visualization */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <FontAwesomeIcon icon={faChartLine} className="mr-2 text-purple-600" />
          Confidence Intervals (Total Cost)
        </h3>
        
        <div className="relative">
          {/* Simple Box Plot Visualization */}
          <div className="mb-6 p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border-2 border-blue-200">
            <div className="relative" style={{ height: '200px', paddingTop: '40px', paddingBottom: '60px' }}>
              
              {/* Horizontal scale line */}
              <div className="absolute" style={{ top: '100px', left: '0', right: '0', height: '2px', background: '#d1d5db' }}></div>
              
              {/* Min marker */}
              <div 
                className="absolute"
                style={{
                  left: '0%',
                  top: '92px',
                  width: '2px',
                  height: '16px',
                  background: '#6b7280'
                }}
              >
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap">
                  Min<br/>{formatCurrency(statistics.totalCost.min)}
                </div>
              </div>

              {/* P10 marker */}
              <div 
                className="absolute"
                style={{
                  left: `${((statistics.totalCost.p10 - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  top: '85px',
                  width: '3px',
                  height: '30px',
                  background: '#ef4444'
                }}
              >
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs text-red-700 font-bold whitespace-nowrap bg-red-50 px-2 py-1 rounded border border-red-300">
                  P10: {formatCurrency(statistics.totalCost.p10)}
                </div>
              </div>

              {/* P25-P75 Box (IQR - Interquartile Range) */}
              <div 
                className="absolute bg-blue-200 border-3 border-blue-500 rounded-lg"
                style={{
                  left: `${((statistics.totalCost.p25 - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  width: `${((statistics.totalCost.p75 - statistics.totalCost.p25) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  top: '80px',
                  height: '40px',
                  border: '3px solid #3b82f6'
                }}
              >
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xs text-blue-900 font-semibold">
                  50% of outcomes
                </div>
              </div>

              {/* Median line (P50) */}
              <div 
                className="absolute bg-purple-600 rounded"
                style={{
                  left: `${((statistics.totalCost.median - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  width: '4px',
                  top: '70px',
                  height: '60px',
                  zIndex: 10
                }}
              >
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 text-xs text-purple-900 font-bold whitespace-nowrap bg-purple-100 px-3 py-1.5 rounded-lg border-2 border-purple-500 shadow-lg">
                  ⭐ Median: {formatCurrency(statistics.totalCost.median)}
                </div>
              </div>

              {/* P90 marker */}
              <div 
                className="absolute"
                style={{
                  left: `${((statistics.totalCost.p90 - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  top: '85px',
                  width: '3px',
                  height: '30px',
                  background: '#f97316'
                }}
              >
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs text-orange-700 font-bold whitespace-nowrap bg-orange-50 px-2 py-1 rounded border border-orange-300">
                  P90: {formatCurrency(statistics.totalCost.p90)}
                </div>
              </div>

              {/* Max marker */}
              <div 
                className="absolute"
                style={{
                  left: '100%',
                  top: '92px',
                  width: '2px',
                  height: '16px',
                  background: '#6b7280',
                  transform: 'translateX(-2px)'
                }}
              >
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap">
                  Max<br/>{formatCurrency(statistics.totalCost.max)}
                </div>
              </div>

              {/* Whisker line from P10 to P25 */}
              <div 
                className="absolute"
                style={{
                  left: `${((statistics.totalCost.p10 - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  width: `${((statistics.totalCost.p25 - statistics.totalCost.p10) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  top: '99px',
                  height: '2px',
                  background: '#ef4444'
                }}
              ></div>

              {/* Whisker line from P75 to P90 */}
              <div 
                className="absolute"
                style={{
                  left: `${((statistics.totalCost.p75 - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  width: `${((statistics.totalCost.p90 - statistics.totalCost.p75) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  top: '99px',
                  height: '2px',
                  background: '#f97316'
                }}
              ></div>

              {/* Deterministic marker (small diamond) */}
              <div 
                className="absolute"
                style={{
                  left: `${((deterministicComparison.totalCost - statistics.totalCost.min) / (statistics.totalCost.max - statistics.totalCost.min)) * 100}%`,
                  top: '140px',
                  transform: 'translateX(-4px)'
                }}
              >
                <div className="w-2 h-2 bg-green-600 transform rotate-45"></div>
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-xs text-green-900 font-semibold whitespace-nowrap bg-green-50 px-2 py-1 rounded border border-green-400">
                  Deterministic: {formatCurrency(deterministicComparison.totalCost)}
                </div>
              </div>

              {/* Legend labels at bottom */}
              <div className="absolute -bottom-10 left-0 right-0 flex justify-between text-xs font-medium">
                <span className="text-red-700">← Lower Cost (Optimistic)</span>
                <span className="text-orange-700">Higher Cost (Conservative) →</span>
              </div>
            </div>

            <div className="mt-12 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              <strong>Box Plot Guide:</strong> The blue box shows where 50% of outcomes fall (P25-P75). 
              The purple line marks the median (P50 - most likely). 
              Red and orange whiskers extend to P10 and P90 (80% confidence range). 
              The green diamond shows the deterministic (single-run) result for comparison.
            </div>
          </div>
        </div>

        {/* Percentile Values Grid */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="text-xs text-red-700 font-medium mb-1">P10 (Optimistic)</div>
            <div className="font-bold text-lg text-red-900">{formatCurrency(statistics.totalCost.p10)}</div>
            <div className="text-xs text-red-600 mt-1">10% below this</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-700 font-medium mb-1">P25 (Lower Quartile)</div>
            <div className="font-bold text-lg text-blue-900">{formatCurrency(statistics.totalCost.p25)}</div>
            <div className="text-xs text-blue-600 mt-1">25% below this</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-400">
            <div className="text-xs text-purple-700 font-medium mb-1">P50 (Median) ⭐</div>
            <div className="font-bold text-lg text-purple-900">{formatCurrency(statistics.totalCost.median)}</div>
            <div className="text-xs text-purple-600 mt-1">Most likely</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-700 font-medium mb-1">P75 (Upper Quartile)</div>
            <div className="font-bold text-lg text-blue-900">{formatCurrency(statistics.totalCost.p75)}</div>
            <div className="text-xs text-blue-600 mt-1">75% below this</div>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-400">
            <div className="text-xs text-orange-700 font-medium mb-1">P90 (Conservative) ⭐⭐</div>
            <div className="font-bold text-lg text-orange-900">{formatCurrency(statistics.totalCost.p90)}</div>
            <div className="text-xs text-orange-600 mt-1">90% confidence</div>
          </div>
        </div>
      </div>

      {/* Distribution Histogram */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <FontAwesomeIcon icon={faChartBar} className="mr-2 text-blue-600" />
          Cost Distribution ({iterations.toLocaleString()} Simulations)
        </h3>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={distribution.totalCosts}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="bin" 
              tickFormatter={(value) => formatCurrency(value)}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis label={{ value: 'Frequency', angle: -90, position: 'insideLeft' }} />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'frequency') {
                  return [(value * 100).toFixed(1) + '%', 'Probability'];
                }
                return [value, 'Count'];
              }}
              labelFormatter={(value) => `Cost: ${formatCurrency(value)}`}
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ddd' }}
            />
            <Legend />
            <Bar dataKey="count" fill="#8b5cf6" name="Number of Simulations" />
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600">Mean</div>
              <div className="font-bold text-gray-900">{formatCurrency(statistics.totalCost.mean)}</div>
            </div>
            <div>
              <div className="text-gray-600">Std Deviation</div>
              <div className="font-bold text-gray-900">{formatCurrency(statistics.totalCost.stdDev)}</div>
            </div>
            <div>
              <div className="text-gray-600">Coefficient of Variation</div>
              <div className="font-bold text-gray-900">
                {((statistics.totalCost.stdDev / statistics.totalCost.mean) * 100).toFixed(2)}%
              </div>
            </div>
            <div>
              <div className="text-gray-600">80% CI Width</div>
              <div className="font-bold text-gray-900">{formatCurrency(confidenceIntervalWidth)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Budget Planning Scenarios
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scenario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  NPV
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recommendation
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="bg-green-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  P10 - Optimistic
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                  {formatCurrency(statistics.totalCost.p10)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(statistics.npv.p10)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    High Risk
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-600">
                  Only 10% chance costs will be this low
                </td>
              </tr>
              
              <tr className="bg-blue-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  P50 - Expected (Median)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                  {formatCurrency(statistics.totalCost.median)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(statistics.npv.median)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    Balanced
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-600">
                  50/50 probability - median outcome
                </td>
              </tr>

              <tr className="bg-purple-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Mean (Average)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                  {formatCurrency(statistics.totalCost.mean)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(statistics.npv.mean)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                    Statistical
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-600">
                  Mathematical average of all iterations
                </td>
              </tr>

              <tr className="bg-orange-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  P90 - Conservative
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                  {formatCurrency(statistics.totalCost.p90)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(statistics.npv.p90)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Low Risk
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-600">
                  90% confidence - recommended for budgeting
                </td>
              </tr>

              <tr className="bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Deterministic (Comparison)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                  {formatCurrency(deterministicComparison.totalCost)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(deterministicComparison.npv)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-800">
                    No Variation
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-600">
                  Single run with base parameters
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Budget Recommendations */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          💡 Budget Planning Recommendations
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1 mr-3" />
            <div>
              <div className="font-semibold text-gray-900">For Standard Planning (50% confidence):</div>
              <div className="text-gray-700">
                Budget <strong>{formatCurrency(statistics.totalCost.median)}</strong> based on the P50 (median) value. 
                This represents the most likely outcome.
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-1 mr-3" />
            <div>
              <div className="font-semibold text-gray-900">For Conservative Planning (80% confidence):</div>
              <div className="text-gray-700">
                Budget <strong>{formatCurrency(statistics.totalCost.p80 || statistics.totalCost.p90)}</strong> based on the P90 value. 
                Only 10% chance costs will exceed this amount.
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-600 mt-1 mr-3" />
            <div>
              <div className="font-semibold text-gray-900">Risk Assessment:</div>
              <div className="text-gray-700">
                The 80% confidence interval width is <strong>{formatCurrency(confidenceIntervalWidth)}</strong> 
                ({confidenceIntervalPercent.toFixed(1)}% of mean cost). 
                This represents the uncertainty in your budget forecast.
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600 mt-1 mr-3" />
            <div>
              <div className="font-semibold text-gray-900">Variance Analysis:</div>
              <div className="text-gray-700">
                Standard deviation is <strong>{formatCurrency(statistics.totalCost.stdDev)}</strong> 
                (CV = {((statistics.totalCost.stdDev / statistics.totalCost.mean) * 100).toFixed(2)}%). 
                {((statistics.totalCost.stdDev / statistics.totalCost.mean) * 100) < 5 
                  ? ' Low variance indicates stable, predictable costs.' 
                  : ((statistics.totalCost.stdDev / statistics.totalCost.mean) * 100) < 10
                  ? ' Moderate variance - typical for bridge maintenance projects.'
                  : ' High variance indicates significant uncertainty - consider more detailed analysis.'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NPV Statistics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Net Present Value (NPV) Analysis
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded">
            <div className="text-xs text-gray-600">Mean NPV</div>
            <div className="text-lg font-bold text-gray-900">{formatCurrency(statistics.npv.mean)}</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded">
            <div className="text-xs text-gray-600">Median NPV</div>
            <div className="text-lg font-bold text-gray-900">{formatCurrency(statistics.npv.median)}</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded">
            <div className="text-xs text-blue-700">P90 NPV</div>
            <div className="text-lg font-bold text-blue-900">{formatCurrency(statistics.npv.p90)}</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded">
            <div className="text-xs text-gray-600">NPV Range</div>
            <div className="text-sm font-bold text-gray-900">
              {formatCurrency(statistics.npv.max - statistics.npv.min)}
            </div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded">
            <div className="text-xs text-green-700">Deterministic NPV</div>
            <div className="text-lg font-bold text-green-900">{formatCurrency(deterministicComparison.npv)}</div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          <strong>NPV vs Total Cost:</strong> NPV accounts for time value of money. 
          NPV of {formatCurrency(statistics.npv.median)} is lower than total cost of {formatCurrency(statistics.totalCost.median)} 
          because future costs are worth less than today's dollars (inflation rate: {(results.scenario.discountRate * 100).toFixed(1)}%).
        </div>
      </div>

      {/* Comparison with Deterministic */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Monte Carlo vs. Deterministic Comparison
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deterministic</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monte Carlo (Mean)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Difference</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% Diff</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Total Cost</td>
                <td className="px-4 py-3 text-sm text-right text-gray-900">
                  {formatCurrency(deterministicComparison.totalCost)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 font-bold">
                  {formatCurrency(statistics.totalCost.mean)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">
                  {formatCurrency(Math.abs(statistics.totalCost.mean - deterministicComparison.totalCost))}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">
                  {(((statistics.totalCost.mean - deterministicComparison.totalCost) / deterministicComparison.totalCost) * 100).toFixed(2)}%
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">NPV</td>
                <td className="px-4 py-3 text-sm text-right text-gray-900">
                  {formatCurrency(deterministicComparison.npv)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 font-bold">
                  {formatCurrency(statistics.npv.mean)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">
                  {formatCurrency(Math.abs(statistics.npv.mean - deterministicComparison.npv))}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">
                  {(((statistics.npv.mean - deterministicComparison.npv) / deterministicComparison.npv) * 100).toFixed(2)}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-700">
          <strong>Interpretation:</strong> Monte Carlo and deterministic results are very close (
          {Math.abs(((statistics.totalCost.mean - deterministicComparison.totalCost) / deterministicComparison.totalCost) * 100).toFixed(2)}% difference),
          which validates the simulation. The Monte Carlo provides additional value by showing the <strong>range of possible outcomes</strong> 
          and <strong>confidence levels</strong> for budget planning.
        </div>
      </div>

      {/* Summary Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-600 mt-0.5 mr-3" />
          <div className="text-sm text-yellow-800">
            <strong>How to Use These Results:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Use <strong>P50 (Median)</strong> for standard budget planning</li>
              <li>Use <strong>P90</strong> for conservative/risk-averse budgeting</li>
              <li>The difference between P90 and P50 represents your <strong>contingency budget</strong></li>
              <li>Standard deviation shows cost predictability - lower is more stable</li>
              <li>Compare with deterministic to validate the model</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonteCarloResults;

